import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi, type PaymentAccount, type PaymentAccountType } from '@/api/portal';

const TYPE_META: Record<PaymentAccountType, { label: string; icon: string; color: string; bg: string; border: string }> = {
  bank: { label: '银行卡', icon: 'ri-bank-card-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  alipay: { label: '支付宝', icon: 'ri-alipay-line', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
  wechat: { label: '微信支付', icon: 'ri-wechat-pay-line', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/20' },
  usdt: { label: 'USDT', icon: 'ri-currency-line', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
};

const TAB_TYPES: { type: PaymentAccountType | 'all'; label: string }[] = [
  { type: 'all', label: '全部' },
  { type: 'bank', label: '银行卡' },
  { type: 'alipay', label: '支付宝' },
  { type: 'wechat', label: '微信' },
  { type: 'usdt', label: 'USDT' },
];

export default function PaymentAccountsPage() {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [activeTab, setActiveTab] = useState<PaymentAccountType | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PaymentAccount | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formType, setFormType] = useState<PaymentAccountType>('bank');
  const [formName, setFormName] = useState('');
  const [formAccount, setFormAccount] = useState('');
  const [formHolder, setFormHolder] = useState('');
  const [formExtra, setFormExtra] = useState('');
  const [formUsdtChain, setFormUsdtChain] = useState('TRC20');
  const [formDefault, setFormDefault] = useState(false);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return accounts;
    return accounts.filter((a) => a.type === activeTab);
  }, [accounts, activeTab]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: accounts.length };
    (['bank', 'alipay', 'wechat', 'usdt'] as PaymentAccountType[]).forEach((t) => {
      c[t] = accounts.filter((a) => a.type === t).length;
    });
    return c;
  }, [accounts]);

  useEffect(() => {
    loadAccounts();
  }, []);

  async function loadAccounts() {
    setLoading(true);
    try {
      const res = await portalApi.paymentAccounts();
      setAccounts(res.accountList || []);
    } catch (error: any) {
      showToast(error.message || '获取提现账号失败', 'error');
    } finally {
      setLoading(false);
    }
  }

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 2500);
  }

  function openAdd(type: PaymentAccountType = 'bank') {
    setEditing(null);
    setFormType(type);
    setFormName('');
    setFormAccount('');
    setFormHolder('');
    setFormExtra('');
    setFormUsdtChain('TRC20');
    setFormDefault(false);
    setShowForm(true);
  }

  function openEdit(acc: PaymentAccount) {
    setEditing(acc);
    setFormType(acc.type);
    setFormName(acc.name);
    setFormAccount(acc.account);
    setFormHolder(acc.holder);
    setFormExtra(acc.extra ?? '');
    setFormUsdtChain(acc.extra === 'ERC20' || acc.extra === 'BEP20' ? acc.extra : 'TRC20');
    setFormDefault(Boolean(acc.isDefault));
    setShowForm(true);
  }

  async function handleSave() {
    if (!formName.trim() || !formAccount.trim() || !formHolder.trim()) {
      showToast('请填写完整信息', 'error');
      return;
    }
    setSaving(true);
    try {
      const extra = formType === 'usdt' ? formUsdtChain : formExtra.trim();
      if (editing) {
        await portalApi.updatePaymentAccount(editing.id, {
          name: formName.trim(),
          account: formAccount.trim(),
          holder: formHolder.trim(),
          extra,
        });
        showToast('账号已更新');
      } else {
        await portalApi.addPaymentAccount({
          type: formType,
          name: formName.trim(),
          account: formAccount.trim(),
          holder: formHolder.trim(),
          extra,
          isDefault: formDefault,
        });
        showToast('账号添加成功');
      }
      setShowForm(false);
      await loadAccounts();
    } catch (error: any) {
      showToast(error.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try {
      await portalApi.deletePaymentAccount(deleteId);
      setDeleteId(null);
      showToast('账号已删除');
      await loadAccounts();
    } catch (error: any) {
      showToast(error.message || '删除失败', 'error');
    }
  }

  async function setDefault(id: number) {
    try {
      await portalApi.setDefaultPaymentAccount(id);
      showToast('默认账号已设置');
      await loadAccounts();
    } catch (error: any) {
      showToast(error.message || '设置失败', 'error');
    }
  }

  async function toggleStatus(acc: PaymentAccount) {
    try {
      await portalApi.updatePaymentAccountStatus(acc.id, acc.status === 'active' ? 'disabled' : 'active');
      await loadAccounts();
    } catch (error: any) {
      showToast(error.message || '状态更新失败', 'error');
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
          {(['bank', 'alipay', 'wechat', 'usdt'] as PaymentAccountType[]).map((t) => {
            const m = TYPE_META[t];
            return (
              <div key={t} className="tech-card p-4 flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${m.bg} border ${m.border}`}>
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${m.icon} ${m.color} text-lg`} />
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{m.label}</p>
                  <p className="text-lg font-bold text-slate-100">{counts[t] || 0}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 bg-slate-800/60 rounded-xl p-1 border border-slate-700/50">
            {TAB_TYPES.map((t) => (
              <button
                key={t.type}
                onClick={() => setActiveTab(t.type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === t.type
                    ? 'bg-slate-700 text-slate-100 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/40'
                }`}
              >
                {t.label}
                {(counts[t.type] || 0) > 0 && (
                  <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                    activeTab === t.type ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-700 text-slate-400'
                  }`}>
                    {counts[t.type]}
                  </span>
                )}
              </button>
            ))}
          </div>

          <button onClick={() => openAdd('bank')} className="tech-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line" />
            </span>
            添加账号
          </button>
        </div>

        {loading ? (
          <div className="tech-card p-10 text-center text-slate-400">正在加载提现账号...</div>
        ) : filtered.length === 0 ? (
          <div className="tech-card p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-slate-800 border border-slate-700/50 mx-auto mb-4">
              <span className="w-6 h-6 flex items-center justify-center text-slate-500">
                <i className="ri-bank-card-line text-xl" />
              </span>
            </div>
            <p className="text-sm text-slate-400 mb-1">暂无该类型账号</p>
            <p className="text-xs text-slate-500 mb-4">点击下方按钮添加您的第一个提现账号</p>
            <button onClick={() => openAdd(activeTab === 'all' ? 'bank' : activeTab)} className="tech-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
              添加账号
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((acc) => {
              const m = TYPE_META[acc.type];
              const isDefault = Boolean(acc.isDefault);
              return (
                <div key={acc.id} className={`tech-card p-5 relative group ${isDefault ? 'tech-border-glow' : ''}`}>
                  {isDefault && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-blue-500/15 text-blue-300 text-[10px] font-medium px-2.5 py-1 rounded-bl-lg rounded-tr-lg border border-blue-500/20">
                        默认
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${m.bg} border ${m.border} shrink-0`}>
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${m.icon} ${m.color} text-lg`} />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-slate-100 truncate">{acc.name}</h4>
                        {acc.status === 'disabled' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-600/20 text-slate-400 border border-slate-600/30">
                            已禁用
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{acc.holder}</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-500 text-xs">账号</span>
                      <span className="text-slate-200 font-mono text-xs truncate">{acc.account}</span>
                    </div>
                    {acc.extra && (
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-500 text-xs">{acc.type === 'usdt' ? '链类型' : '支行/网点'}</span>
                        <span className="text-slate-300 text-xs truncate">{acc.extra}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-slate-700/50">
                    {!isDefault && acc.status === 'active' && (
                      <button
                        onClick={() => setDefault(acc.id)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-300 hover:bg-blue-500/10 border border-transparent hover:border-blue-500/20 transition-all cursor-pointer"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-star-line" /></span>
                        设为默认
                      </button>
                    )}
                    <button
                      onClick={() => toggleStatus(acc)}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer border ${
                        acc.status === 'active'
                          ? 'text-slate-400 hover:text-amber-400 hover:bg-amber-500/5 border-transparent hover:border-amber-500/20'
                          : 'text-green-400 hover:bg-green-500/5 border-transparent hover:border-green-500/20'
                      }`}
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={acc.status === 'active' ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                      </span>
                      {acc.status === 'active' ? '禁用' : '启用'}
                    </button>
                    <button
                      onClick={() => openEdit(acc)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-300 hover:bg-slate-700/60 border border-transparent hover:border-slate-600/40 transition-all cursor-pointer"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-edit-line" /></span>
                      编辑
                    </button>
                    <button
                      onClick={() => setDeleteId(acc.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/5 border border-transparent hover:border-red-500/20 transition-all cursor-pointer"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-delete-bin-line" /></span>
                      删除
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative w-full max-w-md bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60">
              <h3 className="text-sm font-semibold text-slate-100">{editing ? '编辑账号' : '添加提现账号'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-all cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line" /></span>
              </button>
            </div>

            <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {!editing && (
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">账号类型</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['bank', 'alipay', 'wechat', 'usdt'] as PaymentAccountType[]).map((t) => {
                      const m = TYPE_META[t];
                      return (
                        <button
                          key={t}
                          onClick={() => setFormType(t)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                            formType === t
                              ? `${m.bg} ${m.border} ${m.color}`
                              : 'border-slate-700/60 text-slate-400 hover:border-slate-600/60 hover:text-slate-300 bg-slate-800/40'
                          }`}
                        >
                          <span className="w-4 h-4 flex items-center justify-center"><i className={`${m.icon}`} /></span>
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">{formType === 'bank' ? '开户银行' : '账号名称'}</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={formType === 'bank' ? '例如：中国工商银行' : '例如：支付宝'} className="tech-input w-full px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">{formType === 'usdt' ? '钱包地址' : formType === 'bank' ? '银行卡号' : '账号'}</label>
                <input type="text" value={formAccount} onChange={(e) => setFormAccount(e.target.value)} placeholder={formType === 'usdt' ? 'T 开头或 0x 开头' : formType === 'bank' ? '请输入银行卡号' : '请输入账号'} className="tech-input w-full px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">真实姓名</label>
                <input type="text" value={formHolder} onChange={(e) => setFormHolder(e.target.value)} placeholder="请输入持卡人/账户实名" className="tech-input w-full px-3 py-2 text-sm" />
              </div>

              {formType === 'bank' && (
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">开户支行</label>
                  <input type="text" value={formExtra} onChange={(e) => setFormExtra(e.target.value)} placeholder="例如：北京海淀支行" className="tech-input w-full px-3 py-2 text-sm" />
                </div>
              )}

              {formType === 'usdt' && (
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">链类型</label>
                  <div className="flex gap-2">
                    {['TRC20', 'ERC20', 'BEP20'].map((chain) => (
                      <button key={chain} onClick={() => setFormUsdtChain(chain)} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer ${formUsdtChain === chain ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'border-slate-700/60 text-slate-400 hover:border-slate-600/60 bg-slate-800/40'}`}>
                        {chain}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!editing && (
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <input type="checkbox" checked={formDefault} onChange={(e) => setFormDefault(e.target.checked)} className="accent-blue-500" />
                  设为该类型默认提现账号
                </label>
              )}
            </div>

            <div className="flex items-center gap-2 px-5 py-4 border-t border-slate-700/60 bg-slate-800/30">
              <button onClick={() => setShowForm(false)} className="tech-btn-secondary flex-1 py-2 text-sm">取消</button>
              <button disabled={saving} onClick={handleSave} className="tech-btn-primary flex-1 py-2 text-sm disabled:opacity-60">
                {editing ? '保存修改' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
          <div className="relative w-full max-w-sm bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl p-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 mx-auto mb-4">
              <span className="w-6 h-6 flex items-center justify-center text-red-400">
                <i className="ri-delete-bin-line text-xl" />
              </span>
            </div>
            <h3 className="text-sm font-semibold text-slate-100 mb-1">确认删除该账号？</h3>
            <p className="text-xs text-slate-400 mb-5">删除后将无法恢复，相关提现记录不受影响</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteId(null)} className="tech-btn-secondary flex-1 py-2 text-sm">取消</button>
              <button onClick={handleDelete} className="flex-1 py-2 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-500 transition-all cursor-pointer">
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg border flex items-center gap-2 animate-[fadeInUp_0.3s_ease-out] ${
          toast.type === 'success'
            ? 'bg-slate-800 border-green-500/30 text-green-300'
            : 'bg-slate-800 border-red-500/30 text-red-300'
        }`}>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
          </span>
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
